import {
  registerUser,
  trackEvent,
  trackScreenView,
  useDispatch,
  useSelector,
} from '@betty/toolkit/state';
import {
  fetchRegistrationConfig,
  fetchRegistrationOffers,
  selectFirstErroredStepIndex,
  selectRegistrationAutoLoginState,
  selectRegistrationConfig,
  selectRegistrationConfigDataState,
  selectRegistrationFormData,
  selectRegistrationFormDataState,
  selectRegistrationFormSubmitError,
  selectRegistrationOffersDataState,
  selectRegistrationStepIndex,
  selectShouldReturnToSummary,
  setPreventRemove,
  setStepIndex,
  submitRegistrationForm,
} from '../../state';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { BettyCoinLoader, Box } from '@betty/moonlight';
import RegistrationForm from '../RegistrationForm/RegistrationForm';
import { Platform } from 'react-native';
import { PERMISSIONS, request } from 'react-native-permissions';
import { BottomSheet } from '@betty/navigation';
import { ApiError } from '@betty/toolkit/api';
import RegistrationLoader from '../RegistrationLoader/RegistrationLoader';
import RegistrationFailed from '../RegistrationFailed/RegistrationFailed';
import {
  ALLOWED_NAVIGATION_URLS,
  ERROR_BOTTOM_SHEETS,
} from './Registration.constants';
import { useBlocker } from 'react-router-dom';

function Registration() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formDataState = useSelector(selectRegistrationFormDataState);
  const submitErrorCode = useSelector(selectRegistrationFormSubmitError);
  const shouldReturnToSummary = useSelector(selectShouldReturnToSummary);
  const config = useSelector(selectRegistrationConfig);
  const configDataState = useSelector(selectRegistrationConfigDataState);
  const data = useSelector(selectRegistrationFormData);
  const offersDataState = useSelector(selectRegistrationOffersDataState);
  const stepIndex = useSelector(selectRegistrationStepIndex);
  const firstErroredStepIndex = useSelector(selectFirstErroredStepIndex);
  const step = useMemo(
    () => config.steps[stepIndex],
    [stepIndex, config.steps],
  );
  const autoLoginState = useSelector(selectRegistrationAutoLoginState);

  const shouldBlockNavigation = useMemo(() => {
    return !(autoLoginState === 'fulfilled' && formDataState === 'fulfilled');
  }, [autoLoginState, formDataState]);

  const blocker = useBlocker(shouldBlockNavigation);

  const getFirstInvalidStepIndex = useCallback(() => {
    return firstErroredStepIndex;
  }, [firstErroredStepIndex]);

  const requestATTPermission = useCallback(() => {
    if (Platform.OS === 'ios') {
      request(PERMISSIONS.IOS.APP_TRACKING_TRANSPARENCY);
    }
  }, []);

  const submitForm = useCallback(async () => {
    const actionResult = await dispatch(submitRegistrationForm(data));

    if (submitRegistrationForm.fulfilled.match(actionResult)) {
      const result = actionResult.payload;
      const { codeVerifier, authorizationCode } = result.account;
      dispatch(registerUser({ codeVerifier, authorizationCode }));

      return;
    }

    if (!submitRegistrationForm.rejected.match(actionResult)) return;

    const error = actionResult.payload;
    if (!(error instanceof ApiError)) return;

    const { errorCode } = error?.data || {};
    if (!errorCode) return;

    const bottomSheetConfig =
      errorCode in ERROR_BOTTOM_SHEETS
        ? ERROR_BOTTOM_SHEETS[errorCode as keyof typeof ERROR_BOTTOM_SHEETS]
        : null;

    if (bottomSheetConfig) {
      BottomSheet.open(bottomSheetConfig);
    } else {
      BottomSheet.open({
        namespace: 'common',
        type: 'Info',
        data: {
          textNS: 'registration',
          textKey: 'defaultErrorBottomSheetText',
        },
      });
    }
  }, [dispatch, data]);

  const returnToSummary = useCallback(() => {
    const summaryStepIndex = config.steps.findIndex(
      (step) => step.id === 'summary',
    );

    if (summaryStepIndex !== -1) {
      dispatch(setStepIndex(summaryStepIndex));
    }
  }, [config.steps, dispatch]);

  const onStepSubmit = useCallback(() => {
    if (stepIndex < config.steps.length - 1) {
      const nextStepIndex = config.steps
        .slice(stepIndex + 1)
        .findIndex((step) => !step.skip);
      dispatch(setStepIndex(stepIndex + 1 + nextStepIndex));
    } else {
      submitForm();
    }
  }, [stepIndex, config.steps, submitForm, dispatch]);

  useEffect(() => {
    dispatch(fetchRegistrationConfig());
    dispatch(fetchRegistrationOffers());
  }, []);

  useEffect(() => {
    if (
      configDataState === 'fulfilled' &&
      offersDataState.match('fulfilled|rejected')
    ) {
      const index = getFirstInvalidStepIndex();

      dispatch(setStepIndex(index));
    }
  }, [configDataState, offersDataState]);

  useEffect(() => {
    requestATTPermission();
  }, [requestATTPermission]);

  useEffect(() => {
    dispatch(setPreventRemove(true));
  }, [dispatch]);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      const targetPath = blocker.location?.pathname;
      if (targetPath && ALLOWED_NAVIGATION_URLS.includes(targetPath)) {
        blocker.proceed();
      } else {
        if (stepIndex !== 0) {
          const previousStepIndex = stepIndex - 1;
          dispatch(setStepIndex(previousStepIndex));
        } else {
          BottomSheet.open({
            namespace: 'registration',
            type: 'QuitRegistrationBottomSheet',
          });
        }

        blocker.reset();
      }
    }
  }, [blocker, blocker.state, dispatch]);

  useEffect(() => {
    if (!step) {
      return;
    }

    dispatch(
      trackScreenView({
        screenClass: 'registration',
        screenName: step.id,
        screenParams: step,
      }),
    );

    dispatch(
      trackEvent({
        name: 'registration_step',
        params: { stepId: step.id, stepFields: step.fields?.join(',') },
      }),
    );
  }, [step, dispatch]);

  if (
    configDataState.match('idle|pending') ||
    offersDataState.match('idle|pending') ||
    !step
  ) {
    return <BettyCoinLoader />;
  }

  if (autoLoginState === 'pending' || formDataState === 'pending') {
    return (
      <RegistrationLoader
        titleNS="registration"
        titleKey="registrationLoaderTitle"
      />
    );
  }

  if (autoLoginState === 'fulfilled' && formDataState === 'fulfilled') {
    navigate('/', { replace: true });
    return null;
  }

  if (submitErrorCode === 'account_suspended') {
    return <RegistrationFailed />;
  }

  return (
    <Box flex={1}>
      <RegistrationForm
        config={config}
        activeStepId={step.id}
        onStepSubmit={shouldReturnToSummary ? returnToSummary : onStepSubmit}
        submitDataState={formDataState}
      />
    </Box>
  );
}

export default memo(Registration);
